// Phase 1.ζ-2 — freemium locked-state UI shell. Renders the §10.4 cell
// matched against the unlockState tuple returned by deriveUnlockState
// (src/lib/unlockState.js). Pure presentation: state derivation is the
// caller's job, payment side effect is wired through onUnlock.
//
// Cell matrix (§10.4):
//   1. lockoutPassed (any contest_type)       → dead pool, CTA disabled
//   2. !unlocked + awaiting_odds (divisions)  → single card + sub-line
//   3. !unlocked + frozen       (divisions)   → blurred tier preview behind glass card
//   4. !unlocked + non-divisions              → plain CTA-over-empty-state
//
// Only rendered when !unlockState.unlocked. The mode-component gate is
// the gatekeeper; this file never decides whether the pool is unlocked.
//
// Visual language matches the existing golf empty-states (see
// GolfDivisionsMode isAwaiting block at L325 — spacing, font ladder,
// emoji-heading + body + CTA). backdrop-filter: blur(14px) on the
// OUTER glass shell only (Cell 3) per GolfPicksMode L639 perf note —
// per-row blur on a long tier list would chug on mid-range mobile.

function formatPrice(cents) {
  if (cents == null) return null
  const dollars = cents / 100
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

const shellBase = {
  padding: '40px 20px',
  textAlign: 'center',
  fontFamily: 'inherit',
  maxWidth: 380,
  margin: '0 auto',
}

function PrimaryCTA({ onClick, disabled, unlocking, priceLabel }) {
  const label = unlocking
    ? 'Starting checkout…'
    : priceLabel ? `🔒 Unlock for ${priceLabel} to make picks` : '🔒 Unlock to make picks'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || unlocking}
      style={{
        minWidth: 220,
        padding: '13px 22px',
        fontSize: 14,
        fontWeight: 700,
        background: 'var(--gold)',
        color: '#000',
        border: 'none',
        borderRadius: 10,
        cursor: (disabled || unlocking) ? 'wait' : 'pointer',
        opacity: unlocking ? 0.7 : 1,
        boxShadow: '0 6px 18px rgba(0, 230, 118, 0.25)',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  )
}

export default function LockedShell({
  group,
  unlockState,
  contestClass,
  priceCents,
  onUnlock,
  unlocking,
  tierPreview,
}) {
  const { lockoutPassed, divisionsStatus } = unlockState || {}
  const priceLabel = formatPrice(priceCents)

  // ── Cell 1 · Dead pool (terminal §10.6) ────────────────────────
  // lockoutPassed wins over divisions status — once the pool can no
  // longer be unlocked, the tier preview / awaiting copy is moot.
  if (lockoutPassed) {
    return (
      <div style={shellBase}>
        <div style={{ fontSize: 44, marginBottom: 14 }}>🚫</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          This pool can no longer be unlocked.
        </div>
        <div style={{
          fontSize: 12, color: 'var(--text-muted)', fontFamily: 'Arial, sans-serif',
          lineHeight: 1.6, maxWidth: 320, margin: '4px auto 0',
        }}>
          The contest started before anyone paid the unlock fee.
          Per the no-refunds policy, no picks can be made for this
          pool.
        </div>
        <button
          type="button"
          disabled
          style={{
            minWidth: 220, marginTop: 22, padding: '13px 22px',
            fontSize: 14, fontWeight: 600,
            background: 'var(--surface2)', color: 'var(--text-dim)',
            border: '1px solid var(--border)', borderRadius: 10,
            cursor: 'not-allowed', opacity: 0.6, fontFamily: 'inherit',
          }}
        >
          Unlock closed
        </button>
      </div>
    )
  }

  // ── Cell 2 · Divisions awaiting odds ──────────────────────────
  // Single card, single CTA per §10.4 stacked-empty-state rule.
  // Tone borrowed from GolfDivisionsMode isAwaiting (L325) so the
  // freemium gate doesn't visually clash with the awaiting copy.
  if (divisionsStatus === 'awaiting_odds') {
    return (
      <div style={shellBase}>
        <div style={{ fontSize: 44, marginBottom: 14 }}>🔒</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          {priceLabel ? `Unlock for ${priceLabel} to make picks` : 'Unlock to make picks'}
        </div>
        <div style={{
          fontSize: 12, color: 'var(--text-dim)',
          fontFamily: 'Arial, sans-serif', marginBottom: 18, lineHeight: 1.6,
        }}>
          Tiers will appear when odds drop, a few days before
          Round 1. Pay now to enable picks for everyone the moment
          the field firms up.
        </div>
        <PrimaryCTA onClick={onUnlock} unlocking={unlocking} priceLabel={priceLabel} />
      </div>
    )
  }

  // ── Cell 3 · Divisions frozen — blurred tier preview + glass card ──
  // OUTER shell carries the backdrop-filter; per-card blur would chug
  // (GolfPicksMode L639 perf note). Tier labels visible enough that
  // the player sees what they're buying into; selection is gated.
  if (divisionsStatus === 'frozen' && Array.isArray(tierPreview) && tierPreview.length > 0) {
    return (
      <div style={{ position: 'relative', minHeight: 520, padding: '16px 0' }}>
        {/* Preview layer — tier labels visible but greyed/desaturated. */}
        <div aria-hidden style={{
          padding: '8px 16px 120px',
          opacity: 0.45,
          filter: 'saturate(0.4)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}>
          {tierPreview.map((label, i) => (
            <div
              key={i}
              style={{
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                padding: '14px 16px',
                marginBottom: 10,
                fontFamily: 'Arial, sans-serif',
                fontSize: 13,
                color: 'var(--text-dim)',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                {label}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Pick one — roster reveals on unlock
              </div>
            </div>
          ))}
        </div>

        {/* Glass card overlay — backdrop-filter applied here ONCE on the
            outer shell. Per GolfPicksMode L639 perf note, never per-row. */}
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '46%',
          transform: 'translate(-50%, -50%)',
          width: 'min(340px, 92vw)',
          padding: '28px 22px',
          background: 'rgba(20, 20, 20, 0.78)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          textAlign: 'center',
          fontFamily: 'inherit',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          boxShadow: '0 14px 36px rgba(0, 0, 0, 0.55)',
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            {priceLabel ? `Unlock for ${priceLabel} to make picks` : 'Unlock to make picks'}
          </div>
          <div style={{
            fontSize: 12, color: 'var(--text-dim)',
            fontFamily: 'Arial, sans-serif', lineHeight: 1.5, marginBottom: 20,
          }}>
            Tiers are set. Pay once to enable picks, standings,
            and chat for everyone in this pool.
          </div>
          <PrimaryCTA onClick={onUnlock} unlocking={unlocking} priceLabel={priceLabel} />
        </div>
      </div>
    )
  }

  // ── Cell 4 · Non-divisions plain CTA ──────────────────────────
  // Operator-confirmed: no slate preview, no blur of real pick data.
  // Used for UFC, pick'em (daily / weekly / survivor), and bracket.
  return (
    <div style={shellBase}>
      <div style={{ fontSize: 44, marginBottom: 14 }}>🔒</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
        {priceLabel ? `Unlock for ${priceLabel} to make picks` : 'Unlock to make picks'}
      </div>
      <div style={{
        fontSize: 12, color: 'var(--text-muted)',
        fontFamily: 'Arial, sans-serif', lineHeight: 1.6,
        maxWidth: 320, margin: '0 auto 22px',
      }}>
        Pay once to enable picks, standings, and chat for everyone
        in this pool. Single payment, no subscription.
      </div>
      <PrimaryCTA onClick={onUnlock} unlocking={unlocking} priceLabel={priceLabel} />
    </div>
  )
}
